/**
 * Forecast API hooks for HMH Lunch Ordering System.
 */

import { useState, useCallback } from 'react';
import { apiConfig } from '../config';

// Types
interface ForecastComponent {
  dow_average: number;
  menu_item_average: number;
  recent_trend: number;
}

interface ForecastWeights {
  dow_weight: number;
  menu_weight: number;
  trend_weight: number;
}

interface OrderTypeForecast {
  forecast_date: string;
  order_type: string;
  predicted_count: number;
  confidence_low: number;
  confidence_high: number;
  components: ForecastComponent;
  weights: ForecastWeights;
  menu_week: number;
  day_of_week: number;
}

export interface DailyForecast {
  date: string;
  menu_week: number;
  day_of_week: number;
  forecasts: {
    regular: OrderTypeForecast;
    salad: OrderTypeForecast;
    special: OrderTypeForecast;
  };
  total_predicted: number;
}

interface AccuracyRecord {
  date: string;
  order_type: string;
  predicted: number;
  actual: number;
  absolute_error: number;
  percentage_error: number;
}

interface AccuracySummary {
  mean_absolute_error: number;
  mean_percentage_error: number;
  total_forecasts: number;
}

export interface AccuracyData {
  start_date: string;
  end_date: string;
  accuracy_records: AccuracyRecord[];
  summary: AccuracySummary;
}

export interface KitchenPrepData {
  date: string;
  day_name: string;
  menu_week: number;
  regular_menu_item: string | null;
  forecasts: {
    regular: number;
    salad: number;
    special: number;
  };
  total_predicted: number;
  prep_recommendations: string[];
}

interface DayOfWeekPattern {
  regular: number;
  salad: number;
  special: number;
}

interface WeekPattern {
  total_orders: number;
  avg_daily: number;
  days_with_data: number;
}

export interface HistoricalPatterns {
  day_of_week_patterns: Record<string, DayOfWeekPattern>;
  menu_week_patterns: Record<string, WeekPattern>;
  data_range: {
    start: string | null;
    end: string | null;
    total_days: number;
  };
}

// Hook for tomorrow's forecast
export function useTomorrowForecast() {
  const [data, setData] = useState<DailyForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchForecast = useCallback(async (accessToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiConfig.baseUrl}/forecasts/tomorrow`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch forecast');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching tomorrow forecast:', err);
      setError(err instanceof Error ? err.message : 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, fetchForecast, loading, error };
}

// Hook for week forecast
export function useWeekForecast() {
  const [data, setData] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWeekForecast = useCallback(async (accessToken: string, startDate?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = startDate
        ? `${apiConfig.baseUrl}/forecasts/week?start_date=${startDate}`
        : `${apiConfig.baseUrl}/forecasts/week`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch week forecast');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching week forecast:', err);
      setError(err instanceof Error ? err.message : 'Failed to load week forecast');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, fetchWeekForecast, loading, error };
}

// Hook for forecast accuracy
export function useForecastAccuracy() {
  const [data, setData] = useState<AccuracyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccuracy = useCallback(async (accessToken: string, startDate: string, endDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${apiConfig.baseUrl}/forecasts/accuracy?start_date=${startDate}&end_date=${endDate}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch accuracy data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching accuracy:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accuracy data');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, fetchAccuracy, loading, error };
}

// Hook for kitchen prep view
export function useKitchenPrep() {
  const [data, setData] = useState<KitchenPrepData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKitchenPrep = useCallback(async (accessToken: string, targetDate?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = targetDate
        ? `${apiConfig.baseUrl}/forecasts/kitchen-prep?target_date=${targetDate}`
        : `${apiConfig.baseUrl}/forecasts/kitchen-prep`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch kitchen prep data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching kitchen prep:', err);
      setError(err instanceof Error ? err.message : 'Failed to load kitchen prep data');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, fetchKitchenPrep, loading, error };
}

// Hook for historical patterns
export function useHistoricalPatterns() {
  const [data, setData] = useState<HistoricalPatterns | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPatterns = useCallback(async (accessToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiConfig.baseUrl}/forecasts/patterns`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patterns');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching patterns:', err);
      setError(err instanceof Error ? err.message : 'Failed to load patterns');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, fetchPatterns, loading, error };
}
